import { Add } from '@mui/icons-material'
import { Button, Checkbox, TableCell, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { percent } from 'csx'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { BaseRole } from '../../../../../api/DTO'
import { GroupRoleSimpleAssociation } from '../../../../../api/DTOtemp'
import {
	GroupRoleAssociationsClient,
	RolesClient,
} from '../../../../../api/clients/identity'
import { TableColumn } from '../../../../../components/tables/DTO'
import { FilteredTable } from '../../../../../components/tables/FilteredTable'
import { FilterControl } from '../../../../../components/tables/filters/DTO'
import { toastService } from '../../../../../services/notifications/ToastService'
import pluralizeString from '../../../../../utils/PluralizeString'
import AdministrationPageContainer from '../../AdministrationPageContainer'
import { AddValuesDialog } from '../AddValuesDialog'
import { GroupAdministrationPageProps } from '../GroupPage'

export const GroupRolesAdministrationPage = observer(
	({ group, tabLabels, onTabChanged }: GroupAdministrationPageProps) => {
		const groupRoleClient = new GroupRoleAssociationsClient()

		const localStore = useLocalObservable(() => ({
			groupRoles: [] as BaseRole[],
			organizationRoles: [] as BaseRole[],
			// get the roles in the organization that don't belong in the group to display when adding new roles
			get filteredOrganizationRoles() {
				return this.organizationRoles.filter(
					(orgRole) =>
						!this.groupRoles
							.map((groupRole) => groupRole.id)
							.includes(orgRole.id),
				)
			},
			isRequesting: false,
			selectedRoles: [] as number[]
		}))

		const getGroupRoles = useCallback(() => {
			return groupRoleClient
				.GetGroupRoles(group.id)
				.then((response) => response.data.map((v) => v.role))
		}, [])

		const getOrganizationRoles = useCallback(() => {
			const rolesClient = new RolesClient()

			return rolesClient.GetAllRoles().then((response) => response.data)
		}, [])

		useEffect(() => {
			; (async () => {
				const groupRolesPromise = getGroupRoles()
				const organizationRolesPromise = getOrganizationRoles()

				runInAction(() => {
					localStore.isRequesting = true
				})

				const groupRoles = await groupRolesPromise
				const organizationRoles = await organizationRolesPromise

				runInAction(() => {
					localStore.groupRoles = groupRoles
					localStore.organizationRoles = organizationRoles
					localStore.isRequesting = false
				})
			})()

			getGroupRoles()
		}, [group, localStore])

		const handleAddRoles = async (roles: BaseRole[]) => {
			const associations: GroupRoleSimpleAssociation[] = []
			for (const role of roles)
				associations.push({ roleId: role.id, groupId: group.id })

			try {
				runInAction(() => {
					localStore.isRequesting = true
				})

				const addedCount = (
					await groupRoleClient.BulkAddGroupRoleAssociation(associations)
				).data

				const groupRoles = await getGroupRoles()

				runInAction(() => {
					localStore.groupRoles = groupRoles
				})

				toastService.displayToast({
					message: `${pluralizeString(addedCount, 'role')} added`,
					area: 'global',
					severity: 'success',
				})
			} catch {
				toastService.displayToast({
					message: 'Error adding roles',
					area: 'global',
					severity: 'error',
				})
			} finally {
				runInAction(() => {
					localStore.isRequesting = false
				})
			}
		}

		const handleRemoveRoles = async () => {
			try {
				runInAction(() => {
					localStore.isRequesting = true
				})

				// we have to get the association ids of roles to remove
				const { data } = await groupRoleClient.GetGroupRoles(group.id)
				const associationsToRemove = data
					.filter((v) => localStore.selectedRoles.includes(v.roleId))
					.map((v) => v.id)

				const removedCount = (
					await groupRoleClient.BulkDeleteGroupRoleAssociations(
						associationsToRemove,
					)
				).data

				const groupRoles = await getGroupRoles()

				runInAction(() => {
					localStore.groupRoles = groupRoles
				})

				toastService.displayToast({
					message: `${pluralizeString(removedCount, 'role')} removed`,
					area: 'global',
					severity: 'success',
				})

				localStore.selectedRoles = []
			} catch {
				toastService.displayToast({
					message: 'Error removing roles',
					area: 'global',
					severity: 'error',
				})
			} finally {
				runInAction(() => {
					localStore.isRequesting = false
				})
			}
		}

		// select if unselected, unselect if already selected
		const handleSelectOneRole = action((roleId: number) => {
			if (!localStore.selectedRoles.includes(roleId)) {
				localStore.selectedRoles.push(roleId)
				return
			}
			const index = localStore.selectedRoles.indexOf(roleId)
			if (index > -1) localStore.selectedRoles.splice(index, 1)
		})

		// select all if some are unselected, unselect if all are already selected
		const handleSelectAllRoles = action(
			(event: ChangeEvent<HTMLInputElement>) => {
				if (event.target.checked)
					localStore.selectedRoles = localStore.groupRoles.map((role) => role.id)
				else localStore.selectedRoles = []
			},
		)

		const roleIsChecked = useCallback(
			(roleId: number) => {
				return localStore.selectedRoles.includes(roleId)
			},
			[localStore.selectedRoles],
		)

		// header checkbox is checked if all roles are selected
		const headerCheckboxChecked = useCallback(() => {
			return (
				localStore.groupRoles.length === localStore.selectedRoles.length && localStore.groupRoles.length !== 0
			)
		}, [localStore.groupRoles, localStore.selectedRoles])

		// header checkbox is indeterminate if some roles are selected
		const headerCheckboxIndeterminate = useCallback(() => {
			return (
				localStore.selectedRoles.length !== 0 &&
				localStore.groupRoles.length !== localStore.selectedRoles.length
			)
		}, [localStore.groupRoles, localStore.selectedRoles])

		const filterControls: FilterControl[] = useMemo(() => [
			{
				type: 'search',
				name: 'search',
				column: ["friendlyName", "description"]
			}
		], [localStore.groupRoles])

		const columns: TableColumn<BaseRole>[] = [
			{
				renderHeadingDynamic: () =>
					<TableCell key={0} padding="checkbox">
						<Checkbox
							color="primary"
							checked={headerCheckboxChecked()}
							indeterminate={headerCheckboxIndeterminate()}
							onChange={handleSelectAllRoles}
						/>
					</TableCell>,
				renderCell: (role: BaseRole) =>
					<TableCell key={0} padding="checkbox">
						<Checkbox
							color="primary"
							checked={roleIsChecked(role.id)}
							value={roleIsChecked(role.id)}
							onChange={() => handleSelectOneRole(role.id)}
						/>
					</TableCell>
			},
			{
				name: "friendlyName",
				renderHeadingDynamic: () => <TableCell key={1}>
					{localStore.selectedRoles.length === 0 ? "Name" :
						<Button
							color="primary"
							variant="outlined"
							disabled={localStore.isRequesting}
							onClick={handleRemoveRoles}
							size="small"
						>
							Remove
						</Button>
					}
				</TableCell>
			},
			{
				name: "description",
				renderHeadingDynamic: () => <TableCell key={2}>
					{localStore.selectedRoles.length === 0 ? "Description" : ""}
				</TableCell>
			}
		]

		return (
			<AdministrationPageContainer
				title={group.name}
				tabs={tabLabels}
				currentTab={2}
				onTabChanged={onTabChanged}
				actions={
					<GroupRolesSectionButtons
						groupName={group.name}
						onAddRoles={handleAddRoles}
						isRequesting={localStore.isRequesting}
						filteredOrganizationRoles={localStore.filteredOrganizationRoles}
					/>
				}
			>
				<FilteredTable<BaseRole>
					columns={columns}
					allEntries={localStore.groupRoles}
					filterControls={filterControls}
				/>
			</AdministrationPageContainer>
		)
	},
)

type GroupRolesSectionButtonsProps = {
	groupName: string
	filteredOrganizationRoles: BaseRole[]
	onAddRoles: (roles: BaseRole[]) => void
	isRequesting: boolean
}

const GroupRolesSectionButtons = observer(
	(props: GroupRolesSectionButtonsProps) => {
		const [addRoleOpen, setAddRoleOpen] = useState(false)

		const handleOpenAddRole = useCallback(() => {
			setAddRoleOpen(true)
		}, [])

		const handleCloseAddRole = useCallback(() => {
			setAddRoleOpen(false)
		}, [])

		const handleAddRoles = useCallback((roles: BaseRole[]) => {
			props.onAddRoles(roles)
			setAddRoleOpen(false)
		}, [])

		return (
			<div>
				<Button
					color="primary"
					variant="contained"
					startIcon={<Add />}
					onClick={handleOpenAddRole}
					disabled={props.isRequesting}
				>
					Add Role
				</Button>
				<AddValuesDialog<BaseRole>
					open={addRoleOpen}
					onClose={handleCloseAddRole}
					onCommit={handleAddRoles}
					getLabel={(role: BaseRole) => `${role.friendlyName}`}
					renderOption={(role) => {
						return (
							<>
								<Typography color="textPrimary" variant="subtitle2">
									{role.friendlyName}
								</Typography>
								<Typography color="textSecondary" variant="body2">
									{(role.description?.length ?? 0) > 0
										? role.description
										: 'no description'}
								</Typography>
							</>
						)
					}}
					options={props.filteredOrganizationRoles}
					placeholder="Search roles"
					addText="Add Roles"
					header={`Add Roles to ${props.groupName}`}
				/>
			</div>
		)
	},
)

const useStyles = makeStyles((theme) => ({
	groupContentRoot: {
		display: 'flex',
		flexDirection: 'column',

		width: percent(100),
		height: percent(100),

		overflow: 'hidden',
	},
}))
